"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { createPortal } from "react-dom"
import QRCode from "qrcode"
import {
    X, Crown, Check, Minus, ShieldCheck, Sparkles,
    Zap, Star, AlertCircle, Loader2, FileText,
    CheckCircle2, Clock, RefreshCw, ChevronLeft,
    type LucideIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Z } from "@/lib/z-index"
import { useAnimatedClose } from "@/lib/use-animated-close"
import {
    fetchProductDetail,
    orderPay,
    fetchPayStatus,
    cancelOrder,
} from "@/lib/community/community-api"
import type { VipTier } from "@/lib/community/types"
import type {
    ApiProductSkuItem,
    ApiOrderPayCombinedResp,
} from "@/lib/community/community-types-api"

/*  常量  */
const VIP_PRODUCT_ID = 1
const POLL_INTERVAL = 3000
const MAX_POLL_COUNT = 60

/*  套餐类型（含后端 skuId）  */
interface VipPlanWithSku {
    tier: VipTier
    label: string
    price: number
    originalPrice: number
    /** 折算月均价 */
    monthlyPrice: number
    /** 推荐标签 */
    tag?: string
    skuId: number
}

function skuToVipPlan(sku: ApiProductSkuItem): VipPlanWithSku | null {
    const name = sku.skuName.toLowerCase()
    let tier: VipTier
    let tag: string | undefined
    if (name.includes("月") || name.includes("month")) {
        tier = "monthly"
    } else if (name.includes("季") || name.includes("quarter")) {
        tier = "quarterly"
        tag = "最受欢迎"
    } else if (name.includes("年") || name.includes("year")) {
        tier = "yearly"
        tag = "最划算"
    } else {
        return null
    }
    const price = sku.price / 100
    const originalPrice = sku.originalPrice / 100
    const months = tier === "monthly" ? 1 : tier === "quarterly" ? 3 : 12
    const monthlyPrice = Math.round((price / months) * 100) / 100
    return { tier, label: sku.skuName, price, originalPrice, monthlyPrice, tag, skuId: sku.id }
}

const FALLBACK_PLANS: VipPlanWithSku[] = [
    { tier: "monthly", label: "月度会员", price: 29.9, originalPrice: 39.9, monthlyPrice: 29.9, skuId: 0 },
    { tier: "quarterly", label: "季度会员", price: 69.9, originalPrice: 119.7, monthlyPrice: 23.3, tag: "最受欢迎", skuId: 0 },
    { tier: "yearly", label: "年度会员", price: 199.9, originalPrice: 478.8, monthlyPrice: 16.66, tag: "最划算", skuId: 0 },
]

/*  会员特权对比  */
interface PrivilegeItem {
    icon: LucideIcon
    title: string
    free: string
    vip: string
}

const PRIVILEGE_COMPARE: PrivilegeItem[] = [
    { icon: Zap, title: "AI 调用次数", free: "20 次/天", vip: "无限次" },
    { icon: Sparkles, title: "可用模型", free: "基础模型", vip: "全部高级模型" },
    { icon: Star, title: "作品曝光加权", free: "无", vip: "优先推荐" },
    { icon: Crown, title: "专属徽章", free: "无", vip: "VIP 尊贵标识" },
    { icon: Zap, title: "技能市场折扣", free: "无", vip: "9 折优惠" },
]

/*  支付方式  */
type PayTab = "alipay" | "wechat"
const PAY_TABS = [
    { id: "alipay" as PayTab, label: "支付宝", payWay: "ALI_OC" as const, desc: "使用支付宝 App 扫码完成支付" },
    { id: "wechat" as PayTab, label: "微信支付", payWay: "WX_NATIVE" as const, desc: "使用微信 App 扫码完成支付" },
]

type Step = "select" | "creating" | "cashier" | "success" | "timeout"

/*  格式化倒计时  */
function formatCountdown(seconds: number) {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0")
    const s = (seconds % 60).toString().padStart(2, "0")
    return { m, s }
}

/*  VIP 协议内容  */
const AGREEMENT_SECTIONS = [
    {
        title: "一、服务说明",
        content: "SafeClaw VIP 会员服务（以下简称\u300c本服务\u300d）由 SafeClaw 平台提供。用户在购买并开通 VIP 会员后，即可享受本协议约定的专属权益。本服务仅限购买账号本人使用，不得转让、出借或共享。",
    },
    {
        title: "二、会员权益",
        content: ["（1）AI 能力无限次调用", "（2）优先使用全部高级 AI 模型", "（3）作品发布获得更高权重推荐", "（4）专属 VIP 徽章及个人主页尊贵标识", "（5）技能市场九折购买优惠", "（6）专属客服通道"],
        note: "平台保留在合理范围内调整权益内容的权利，并提前在平台公告。",
    },
    { title: "三、收费与续费", content: "VIP 会员按套餐周期收费（月度 / 季度 / 年度）。到期后服务自动终止，不自动续费。如需继续使用，请在到期前手动续费。所有费用一经支付，因个人原因造成的未使用部分不予退还，但平台服务故障导致的损失除外。" },
    { title: "四、退款政策", content: "自购买之日起 3 个自然日内，如您尚未实际使用任何 VIP 专属权益，可申请全额退款。超出退款期或已使用过专属权益的，不支持退款。退款申请请通过平台客服提交，我们将在 5 个工作日内处理。" },
    { title: "五、账号安全", content: "用户应妥善保管账号信息，不得将账号用于任何违法违规行为。因用户自身原因导致账号被封禁的，VIP 会员资格同步终止，已缴费用不予退还。如发现账号被盗用，请立即联系客服处理。" },
    { title: "六、知识产权", content: "平台提供的所有 AI 模型、技能市场内容及相关服务均受知识产权法律保护。VIP 会员权益仅供个人合法使用，不得用于商业批量处理、爬虫抓取、二次销售等行为。" },
    { title: "七、免责声明", content: "平台将尽力保证服务稳定，但因不可抗力、网络故障、第三方服务中断等原因导致的服务中断，平台不承担赔偿责任，但会在条件允许时顺延会员有效期。平台 AI 生成内容仅供参考，不构成专业建议，用户自行承担使用风险。" },
    { title: "八、协议修改", content: "SafeClaw 有权根据业务需要修改本协议，修改后的协议将在平台公告，用户继续使用本服务即视为同意修改后的协议内容。如有重大变更，平台将通过站内信或邮件提前通知。" },
    { title: "九、争议解决", content: "本协议的订立、履行和解释均适用中华人民共和国法律。如发生争议，双方应首先协商解决；协商不成的，提交平台注册地有管辖权的人民法院诉讼解决。" },
]

/*  协议弹窗  */
function AgreementModal({ onClose }: { onClose: () => void }) {
    const { closing, requestClose } = useAnimatedClose(onClose, 240)
    const [entered, setEntered] = useState(false)

    useEffect(() => {
        const raf = requestAnimationFrame(() => setEntered(true))
        return () => cancelAnimationFrame(raf)
    }, [])

    const panelStyle: React.CSSProperties = {
        opacity: closing ? 0 : entered ? 1 : 0,
        transform: closing ? "scale(0.97) translateY(6px)" : entered ? "scale(1) translateY(0)" : "scale(0.94) translateY(10px)",
        filter: closing ? "blur(3px)" : entered ? "blur(0)" : "blur(4px)",
        transition: "opacity 240ms, transform 240ms, filter 240ms",
        transitionTimingFunction: closing ? "cubic-bezier(0.4, 0, 1, 1)" : "cubic-bezier(0.16, 1, 0.3, 1)",
    }

    return createPortal(
        <>
            <div
                className={cn(
                    "liquid-glass-overlay fixed inset-0 transition-all duration-300",
                    closing ? "opacity-0 backdrop-blur-none" : entered ? "opacity-100 backdrop-blur-xl" : "opacity-0 backdrop-blur-none",
                )}
                style={{ zIndex: Z.MODAL_NESTED, pointerEvents: closing || !entered ? "none" : "auto" }}
                onClick={requestClose}
                aria-hidden
            />
            <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_NESTED + 1, pointerEvents: "none" }}>
                <div className="w-full max-w-lg" style={{ ...panelStyle, pointerEvents: "auto" }}>
                    <div
                        className="liquid-glass-modal rounded-2xl flex flex-col max-h-[80vh]"
                        style={{ boxShadow: "0 0 0 0.5px color-mix(in oklch, white 10%, transparent) inset,0 1px 1px 0 color-mix(in oklch, white 20%, transparent) inset,0 -1px 1px 0 color-mix(in oklch, black 5%, transparent) inset" }}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
                            <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <h2 className="text-sm font-semibold text-foreground">VIP 会员服务协议</h2>
                            </div>
                            <button type="button" onClick={requestClose} aria-label="关闭协议弹窗" className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        <div className="overflow-y-auto px-6 py-5 space-y-5">
                            <p className="text-[11px] text-muted-foreground/60 leading-relaxed">最后更新日期：2026 年 4 月 15 日 &nbsp;&nbsp; 生效日期：2026 年 4 月 15 日</p>
                            {AGREEMENT_SECTIONS.map((sec) => (
                                <div key={sec.title} className="space-y-1.5">
                                    <h3 className="text-xs font-semibold text-foreground">{sec.title}</h3>
                                    {Array.isArray(sec.content) ? (
                                        <ul className="space-y-1">
                                            {sec.content.map((line) => (
                                                <li key={line} className="text-[12px] text-muted-foreground leading-relaxed">{line}</li>
                                            ))}
                                            {"note" in sec && sec.note && (
                                                <li className="text-[11px] text-muted-foreground/60 leading-relaxed pt-0.5">{sec.note}</li>
                                            )}
                                        </ul>
                                    ) : (
                                        <p className="text-[12px] text-muted-foreground leading-relaxed">{sec.content as string}</p>
                                    )}
                                </div>
                            ))}
                            <p className="text-[11px] text-muted-foreground/50 pt-2 border-t border-border">
                                如您对本协议有任何疑问，请通过平台客服联系我们。<br />
                                售后服务邮箱：<a href="mailto:wangxudong@clonebot.ai" className="text-amber-600 dark:text-amber-400 hover:underline underline-offset-2">wangxudong@clonebot.ai</a>
                            </p>
                        </div>
                        <div className="px-6 py-4 border-t border-border shrink-0">
                            <button type="button" onClick={requestClose} className="w-full h-9 flex items-center justify-center rounded-xl bg-secondary hover:bg-secondary/80 text-foreground text-sm font-medium transition-colors">
                                我已阅读
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body,
    )
}

/*  收银台  */
interface CashierViewProps {
    productId: number
    skuId?: number
    plan: VipPlanWithSku
    onSuccess: () => void
    onTimeout: () => void
    onBack: () => void
}

function CashierView({ productId, skuId, plan, onSuccess, onTimeout, onBack }: CashierViewProps) {
    const [payTab, setPayTab] = useState<PayTab>("alipay")
    const [payInfo, setPayInfo] = useState<ApiOrderPayCombinedResp | null>(null)
    const [qrDataUrl, setQrDataUrl] = useState("")
    const [payLoading, setPayLoading] = useState(false)
    const [payError, setPayError] = useState("")
    const [countdown, setCountdown] = useState(0)
    const [showCancelConfirm, setShowCancelConfirm] = useState(false)
    const [activeOrder, setActiveOrder] = useState<ApiOrderPayCombinedResp | null>(null)
    const pollCountRef = useRef(0)
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const activeOrderRef = useRef<ApiOrderPayCombinedResp | null>(null)

    /* 倒计时初始化由 loadQr 拿到 expiredAt 后设置 */

    /* 倒计时 ticker */
    useEffect(() => {
        if (countdown <= 0) return
        countdownTimerRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) { clearInterval(countdownTimerRef.current!); onTimeout(); return 0 }
                return prev - 1
            })
        }, 1000)
        return () => clearInterval(countdownTimerRef.current!)
    }, [countdown, onTimeout])

    const stopPoll = useCallback(() => {
        if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null }
        pollCountRef.current = 0
    }, [])

    const startPoll = useCallback(() => {
        stopPoll()
        pollTimerRef.current = setInterval(async () => {
            pollCountRef.current += 1
            if (pollCountRef.current > MAX_POLL_COUNT) { stopPoll(); onTimeout(); return }
            try {
                const orderNo = activeOrderRef.current?.orderNo
                if (!orderNo) return
                const res = await fetchPayStatus(orderNo)
                if (res.payStatus === 2) { stopPoll(); onSuccess() }
                if (res.payStatus === 3) { stopPoll(); onTimeout() }
            } catch { /* 网络抖动忽略 */ }
        }, POLL_INTERVAL)
    }, [stopPoll, onSuccess, onTimeout])

    const loadingRef = useRef(false)

    /** 取消当前订单（如有），并清空订单引用 */
    const cancelCurrentOrder = useCallback(async () => {
        const orderNo = activeOrderRef.current?.orderNo
        if (!orderNo) return
        activeOrderRef.current = null
        try { await cancelOrder(orderNo, "用户取消") } catch { /* 忽略网络错误 */ }
    }, [])

    const loadQr = useCallback(async (tab: PayTab) => {
        if (loadingRef.current) return
        loadingRef.current = true
        const cfg = PAY_TABS.find((t) => t.id === tab)!
        setPayLoading(true); setPayError(""); setQrDataUrl(""); setPayInfo(null); stopPoll()
        /* 切换支付方式前取消旧订单 */
        await cancelCurrentOrder()
        try {
            /* 每次（含切换支付方式）调用合并接口重新下单并发起支付 */
            const resp = await orderPay({
                productId,
                skuId,
                quantity: 1,
                payWay: cfg.payWay,
            })
            if (!resp.payData) {
                setPayError("获取支付二维码失败，请点击下方刷新重试")
                return
            }
            setPayInfo(resp)
            setActiveOrder(resp)
            activeOrderRef.current = resp
            /* 设置倒计时 */
            const expiry = new Date(resp.expiredAt).getTime()
            setCountdown(Math.max(0, Math.floor((expiry - Date.now()) / 1000)))
            if (resp.payDataType === "codeImgUrl") {
                setQrDataUrl(resp.payData)
            } else {
                const dataUrl = await QRCode.toDataURL(resp.payData, { width: 160, margin: 1, color: { dark: "#000000", light: "#ffffff" } })
                setQrDataUrl(dataUrl)
            }
            startPoll()
        } catch (e: unknown) {
            setPayError(e instanceof Error ? e.message : "获取支付二维码失败，请重试")
        } finally {
            setPayLoading(false)
            loadingRef.current = false
        }
    }, [productId, skuId, stopPoll, startPoll, cancelCurrentOrder])

    /* 仅在 tab 切换时重新获取，挂载时执行一次 */
    const prevTabRef = useRef<PayTab | null>(null)
    useEffect(() => {
        if (prevTabRef.current === payTab) return
        prevTabRef.current = payTab
        void loadQr(payTab)
        return () => stopPoll()
    }, [payTab, loadQr, stopPoll])

    const { m, s } = formatCountdown(countdown)
    const currentTab = PAY_TABS.find((t) => t.id === payTab)!

    return (
        <div className="flex flex-col relative">
            {/* 收银台头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
                <div className="flex items-center gap-2">
                    <button type="button" aria-label="返回套餐选择" onClick={() => setShowCancelConfirm(true)} className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500">
                        <Crown className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-foreground leading-none">收银台</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{plan.label}</p>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    <span>支付剩余</span>
                    <span className="font-mono font-semibold text-foreground">
                        <span className="inline-flex items-center justify-center px-1 py-0.5 bg-foreground/[0.06] rounded text-xs">{m}</span>
                        <span className="mx-0.5">:</span>
                        <span className="inline-flex items-center justify-center px-1 py-0.5 bg-foreground/[0.06] rounded text-xs">{s}</span>
                    </span>
                </div>
            </div>

            {/* 金额 & 订单号 */}
            <div className="text-center pt-5 pb-3 px-6">
                <p className="text-3xl font-bold text-foreground">
                    <span className="text-xl font-medium mr-0.5"></span>
                    {activeOrder ? (activeOrder.payAmount / 100).toFixed(2) : (plan.price).toFixed(2)}
                </p>
                <p className="text-[11px] text-muted-foreground/60 mt-1">
                    {activeOrder ? `订单编号：${activeOrder.orderNo}` : "正在获取支付信息…"}
                </p>
            </div>

            {/* 支付方式 Tab */}
            <div className="px-6">
                <div className="flex border-b border-border">
                    {PAY_TABS.map((tab, idx) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setPayTab(tab.id)}
                            className={cn(
                                "relative flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors",
                                payTab === tab.id ? "text-foreground" : "text-muted-foreground hover:text-foreground",
                            )}
                        >
                            {idx === 0 && (
                                <span className="absolute -top-2.5 right-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-tl-none rounded-br-none rounded-full bg-red-500 text-white leading-none">推荐</span>
                            )}
                            <span className={cn("flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white", tab.id === "alipay" ? "bg-[#1677FF]" : "bg-[#07C160]")}>
                                {tab.id === "alipay" ? "支" : "微"}
                            </span>
                            {tab.label}
                            {payTab === tab.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500 rounded-full" />}
                        </button>
                    ))}
                </div>
            </div>

            {/* 二维码区域 */}
            <div className="flex flex-col items-center justify-center py-7 px-6 min-h-[210px]">
                {payLoading ? (
                    <div className="flex flex-col items-center gap-3 text-muted-foreground">
                        <Loader2 className="w-8 h-8 animate-spin" />
                        <p className="text-xs">正在获取支付二维码</p>
                    </div>
                ) : payError ? (
                    <div className="flex flex-col items-center gap-3">
                        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-500">
                            <AlertCircle className="w-4 h-4 shrink-0" />
                            {payError}
                        </div>
                        <button type="button" onClick={() => loadQr(payTab)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                            <RefreshCw className="w-3.5 h-3.5" />重新获取
                        </button>
                    </div>
                ) : qrDataUrl ? (
                    <div className="flex flex-col items-center gap-3">
                        <div className="p-2 rounded-xl bg-white shadow-sm ring-1 ring-border">
                            <img src={qrDataUrl} alt="支付二维码" width={140} height={140} className="block" />
                        </div>
                        <p className="text-xs text-muted-foreground">{currentTab.desc}</p>
                        {payInfo && (
                            <button type="button" onClick={() => loadQr(payTab)} className="flex items-center gap-1 text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors">
                                <RefreshCw className="w-3 h-3" />刷新二维码
                            </button>
                        )}
                    </div>
                ) : null}
            </div>

            {/* 安全标识 */}
            <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground/50 pb-5">
                <ShieldCheck className="h-3.5 w-3.5" />
                安全加密支付  随时取消续费
            </div>

            {/* 取消确认遮罩 */}
            {showCancelConfirm && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-black/50 backdrop-blur-sm">
                    <div className="mx-6 w-full max-w-xs bg-background rounded-2xl shadow-xl p-6 flex flex-col items-center text-center">
                        <p className="text-base font-semibold text-foreground mb-1.5">确定离开收银台？</p>
                        <p className="text-sm text-muted-foreground mb-6">订单尚未完成支付，离开将取消本次下单。</p>
                        <div className="flex items-center gap-3 w-full">
                            <button
                                type="button"
                                onClick={async () => {
                                    setShowCancelConfirm(false)
                                    stopPoll()
                                    await cancelCurrentOrder()
                                    onBack()
                                }}
                                className="flex-1 h-9 rounded-xl border border-border text-sm text-muted-foreground hover:bg-secondary transition-colors"
                            >
                                确认离开
                            </button>
                            <button type="button" onClick={() => setShowCancelConfirm(false)} className="flex-1 h-9 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium hover:from-amber-400 hover:to-orange-400 transition-all">
                                继续支付
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

/*  支付成功  */
function SuccessView({ plan, onClose }: { plan: VipPlanWithSku; onClose: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center px-6 py-14 text-center gap-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <div>
                <p className="text-lg font-semibold text-foreground">支付成功</p>
                <p className="text-sm text-muted-foreground mt-1">{plan.label}已开通，享受尊享特权吧！</p>
            </div>
            <button type="button" onClick={onClose} className="mt-2 h-10 px-8 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white text-sm font-semibold transition-all">
                开始体验
            </button>
        </div>
    )
}

/*  超时  */
function TimeoutView({ onReorder }: { onReorder: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center px-6 py-14 text-center gap-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
                <Clock className="w-8 h-8 text-muted-foreground" />
            </div>
            <div>
                <p className="text-lg font-semibold text-foreground">订单已取消</p>
                <p className="text-sm text-muted-foreground mt-1">交易超时已关闭，请重新下单支付。</p>
            </div>
            <button type="button" onClick={onReorder} className="mt-2 h-10 px-8 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white text-sm font-semibold transition-all">
                重新下单
            </button>
        </div>
    )
}

/* 
   主组件
    */

interface VipSubscribeModalProps {
    isOpen: boolean
    onClose: () => void
}

export function VipSubscribeModal({ isOpen, onClose }: VipSubscribeModalProps) {
    const { closing, requestClose, reset } = useAnimatedClose(onClose, 280)
    const [entered, setEntered] = useState(false)
    const [selectedTier, setSelectedTier] = useState<VipTier>("quarterly")
    const [step, setStep] = useState<Step>("select")
    const [createError, setCreateError] = useState("")
    const [showAgreement, setShowAgreement] = useState(false)
    const [vipPlans, setVipPlans] = useState<VipPlanWithSku[]>(FALLBACK_PLANS)
    const [plansLoading, setPlansLoading] = useState(false)
    useEffect(() => {
        if (!isOpen) return
        setPlansLoading(true)
        fetchProductDetail(VIP_PRODUCT_ID)
            .then((product) => {
                const plans = product.skus.map(skuToVipPlan).filter((p): p is VipPlanWithSku => p !== null)
                if (plans.length > 0) setVipPlans(plans)
            })
            .catch(() => { /* 保留兜底 */ })
            .finally(() => setPlansLoading(false))
    }, [isOpen])

    useEffect(() => {
        if (isOpen) {
            reset(); setEntered(false); setStep("select"); setCreateError(""); setSelectedTier("quarterly")
            const raf = requestAnimationFrame(() => setEntered(true))
            const prev = document.body.style.overflow
            document.body.style.overflow = "hidden"
            return () => { cancelAnimationFrame(raf); document.body.style.overflow = prev }
        }
    }, [isOpen, reset])

    const selectedPlan = vipPlans.find((p) => p.tier === selectedTier) ?? vipPlans[0]!

    /** 点击「立即开通」直接进入收银台，由收银台内部调合并接口 */
    const handleOpenCashier = useCallback(() => {
        if (!selectedPlan) return
        setCreateError("")
        setStep("cashier")
    }, [selectedPlan])

    const panelStyle: React.CSSProperties = {
        opacity: closing ? 0 : entered ? 1 : 0,
        transform: closing ? "scale(0.96) translateY(8px)" : entered ? "scale(1) translateY(0)" : "scale(0.92) translateY(12px)",
        filter: closing ? "blur(4px)" : entered ? "blur(0)" : "blur(6px)",
        transition: "opacity 280ms, transform 280ms, filter 280ms",
        transitionTimingFunction: closing ? "cubic-bezier(0.4, 0, 1, 1)" : "cubic-bezier(0.16, 1, 0.3, 1)",
    }

    if (!isOpen) return null

    return createPortal(
        <>
            <div
                className={cn(
                    "liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out",
                    closing ? "opacity-0 backdrop-blur-none" : entered ? "opacity-100 backdrop-blur-xl" : "opacity-0 backdrop-blur-none",
                )}
                style={{ zIndex: Z.MODAL_OVERLAY, pointerEvents: closing || !entered ? "none" : "auto" }}
                onClick={step === "cashier" ? undefined : requestClose}
                aria-hidden
            />

            <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: Z.MODAL_CONTENT, pointerEvents: "none" }}>
                <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ ...panelStyle, pointerEvents: "auto" }}>
                    <div
                        className="liquid-glass-modal rounded-2xl flex flex-col relative overflow-hidden"
                        style={{ boxShadow: "0 0 0 0.5px color-mix(in oklch, white 10%, transparent) inset,0 1px 1px 0 color-mix(in oklch, white 20%, transparent) inset,0 -1px 1px 0 color-mix(in oklch, black 5%, transparent) inset" }}
                    >
                        {/*  支付成功  */}
                        {step === "success" && (
                            <>
                                <div className="flex items-center justify-end px-6 py-4 border-b border-border">
                                    <button type="button" onClick={requestClose} aria-label="关闭" className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                                <SuccessView plan={selectedPlan} onClose={requestClose} />
                            </>
                        )}

                        {/*  超时  */}
                        {step === "timeout" && (
                            <>
                                <div className="flex items-center justify-end px-6 py-4 border-b border-border">
                                    <button type="button" onClick={requestClose} aria-label="关闭" className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                                <TimeoutView onReorder={() => { setStep("select") }} />
                            </>
                        )}

                        {/*  收银台  */}
                        {step === "cashier" && (
                            <CashierView
                                productId={VIP_PRODUCT_ID}
                                skuId={selectedPlan?.skuId || undefined}
                                plan={selectedPlan}
                                onSuccess={() => setStep("success")}
                                onTimeout={() => setStep("timeout")}
                                onBack={() => setStep("select")}
                            />
                        )}

                        {/*  套餐选择  */}
                        {(step === "select" || step === "creating") && (
                            <>
                                <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500">
                                            <Crown className="w-4 h-4 text-white" />
                                        </div>
                                        <h2 className="text-base font-semibold text-foreground">开通 VIP 会员</h2>
                                    </div>
                                    <button type="button" onClick={requestClose} aria-label="关闭" className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>

                                <div className="px-6 py-5 space-y-5">
                                    {/* 套餐选择 */}
                                    <div className="space-y-3">
                                        <p className="text-sm font-medium text-foreground">选择套餐</p>
                                        <div className="grid grid-cols-3 gap-3">
                                            {plansLoading
                                                ? Array.from({ length: 3 }).map((_, i) => (
                                                    <div key={i} className="h-24 rounded-xl border-2 border-border animate-pulse bg-secondary/40" />
                                                ))
                                                : vipPlans.map((plan) => (
                                                    <button
                                                        type="button"
                                                        key={plan.tier}
                                                        onClick={() => { setSelectedTier(plan.tier); setCreateError("") }}
                                                        className={cn(
                                                            "relative flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all",
                                                            selectedTier === plan.tier
                                                                ? "border-amber-500 bg-amber-500/5 shadow-[0_0_12px_rgba(245,158,11,0.15)]"
                                                                : "border-border hover:border-foreground/20 hover:bg-secondary/50",
                                                        )}
                                                    >
                                                        {plan.tag && (
                                                            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white">
                                                                {plan.tag}
                                                            </span>
                                                        )}
                                                        <span className="text-xs text-muted-foreground mt-1">{plan.label}</span>
                                                        <span className="text-lg font-bold text-foreground">{plan.price}元</span>
                                                        {plan.originalPrice > plan.price && (
                                                            <span className="text-[10px] text-muted-foreground/60 line-through">{plan.originalPrice}元</span>
                                                        )}
                                                        <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium"> {plan.monthlyPrice}元/月</span>
                                                    </button>
                                                ))}
                                        </div>
                                    </div>

                                    {/* 特权对比 */}
                                    <div className="space-y-2.5">
                                        <p className="text-sm font-medium text-foreground">会员特权对比</p>
                                        <div className="liquid-glass-surface rounded-xl overflow-hidden">
                                            <div className="grid grid-cols-[1fr_90px_90px] items-center px-4 py-2.5 border-b border-border/60 bg-foreground/[0.02]">
                                                <span className="text-[11px] font-medium text-muted-foreground">特权</span>
                                                <span className="text-[11px] font-medium text-muted-foreground text-center">免费</span>
                                                <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 text-center">VIP</span>
                                            </div>
                                            {PRIVILEGE_COMPARE.map((item, idx) => (
                                                <div
                                                    key={item.title}
                                                    className={cn(
                                                        "grid grid-cols-[1fr_90px_90px] items-center px-4 py-2.5",
                                                        idx < PRIVILEGE_COMPARE.length - 1 && "border-b border-border/40",
                                                    )}
                                                >
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <item.icon className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
                                                        <span className="text-xs text-foreground truncate">{item.title}</span>
                                                    </div>
                                                    <div className="flex items-center">
                                                        {item.free === "无"
                                                            ? <Minus className="w-3.5 h-3.5 text-muted-foreground/30" />
                                                            : <span className="text-[11px] text-muted-foreground">{item.free}</span>
                                                        }
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
                                                        <span className="text-[11px] font-medium text-foreground">{item.vip}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* 错误提示 */}
                                    {createError && (
                                        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                                            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                                            <p className="text-xs text-red-500">{createError}</p>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground/60">
                                        <ShieldCheck className="h-3.5 w-3.5" />
                                        安全加密支付  随时取消续费
                                    </div>
                                </div>

                                <div className="px-6 py-4 border-t border-border shrink-0">
                                    <button
                                        type="button"
                                        disabled={plansLoading}
                                        onClick={handleOpenCashier}
                                        className={cn(
                                            "w-full h-11 flex items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-all",
                                            "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white shadow-lg shadow-amber-500/20",
                                            plansLoading && "opacity-70 pointer-events-none",
                                        )}
                                    >
                                        <Crown className="w-4 h-4" />立即开通  {selectedPlan?.price} 元
                                    </button>
                                    <p className="text-center text-[10px] text-muted-foreground/50 mt-2">
                                        开通即表示同意
                                        <button type="button" onClick={() => setShowAgreement(true)} className="text-amber-600 dark:text-amber-400 hover:underline underline-offset-2 mx-0.5">
                                            《VIP 会员服务协议》
                                        </button>
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {showAgreement && <AgreementModal onClose={() => setShowAgreement(false)} />}
        </>,
        document.body,
    )
}
